import { useState } from "react";
import { motion } from "framer-motion";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { cambiarClave } from "@/lib/api";
import { AVISOS, revisa } from "@/lib/clave.mjs";
import { useAuth } from "@/context/AuthContext";

/** La primera contraseña de verdad, para las cuentas dadas de alta en clase.
 *
 * POR QUE ES UNA PANTALLA Y NO UN AVISO. La contraseña con la que ha entrado venía impresa
 * en una tira de papel que ha dado la vuelta al aula: la sabe quien la repartió, quien se
 * sentó al lado y quien pasó por detrás. Mientras siga valiendo, la cuenta no es de nadie.
 * Un aviso que se puede cerrar se cierra, así que aquí no hay cómo seguir sin cambiarla.
 *
 * Quien la pinta es `ProtectedRoute`, en vez de una ruta propia: así cubre TODAS las
 * páginas con sesión de una vez. Una ruta nueva habría dejado fuera la que se olvidara.
 *
 * AQUI NO SE PIDE LA ANTERIOR, y en Ajustes sí. No es un descuido: la anterior venía impresa
 * en el papel que dio la vuelta al aula, así que pedirla no comprueba nada —la sabe media
 * clase—. Las condiciones de las dos pantallas viven juntas en `lib/clave.mjs` para que no
 * se separen con el tiempo; lo que cambia es el `primeraVez`.
 */
export default function CambiarClave() {
  const { refresh } = useAuth();
  const [clave, setClave] = useState("");
  const [repetida, setRepetida] = useState("");
  const [guardando, setGuardando] = useState(false);

  const { puede: valida, avisos } = revisa({ nueva: clave, repetida, primeraVez: true });
  const puede = valida && !guardando;

  const enviar = async (e) => {
    e.preventDefault();
    if (!puede) return;
    setGuardando(true);
    try {
      await cambiarClave(clave);
      await refresh();          // el aviso se apaga en el backend; esto lo trae al navegador
      toast.success("Contraseña cambiada. Ya es solo tuya.");
    } catch {
      toast.error("No se pudo cambiar la contraseña.");
      setGuardando(false);
    }
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white min-h-[calc(100vh-4rem)] dvh:min-h-[calc(100svh-4rem)] flex items-center justify-center px-6"
      data-testid="page-cambiar-clave"
    >
      <form onSubmit={enviar} className="w-full max-w-[460px]">
        <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/40 mb-4 flex items-center gap-2">
          <KeyRound className="w-3.5 h-3.5" /> Primer acceso
        </div>
        <h1 className="font-serif-display text-4xl leading-tight mb-3">Elige tu contraseña</h1>
        <p className="text-alpha-black/60 mb-8">
          La que te han dado estaba escrita en un papel, así que la sabe más gente que tú.
          Pon una nueva y esta cuenta será solo tuya.
        </p>

        <label className="block font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/50 mb-2">
          Contraseña nueva
        </label>
        <input
          type="password"
          autoFocus
          value={clave}
          onChange={(e) => setClave(e.target.value)}
          data-testid="clave-nueva"
          className="rounded-caja w-full border border-alpha-black/20 focus:border-alpha-black outline-none px-3 py-2.5 mb-1"
        />
        <div className="text-xs mb-5 h-4">
          <span className={avisos.nueva ? "text-alpha-red" : "text-alpha-black/40"}>
            {avisos.nueva || AVISOS.corta}
          </span>
        </div>

        <label className="block font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/50 mb-2">
          Repítela
        </label>
        <input
          type="password"
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
          data-testid="clave-repetida"
          className="rounded-caja w-full border border-alpha-black/20 focus:border-alpha-black outline-none px-3 py-2.5 mb-1"
        />
        <div className="text-xs text-alpha-red mb-6 h-4">
          {avisos.repetida || ""}
        </div>

        <button
          type="submit"
          disabled={!puede}
          data-testid="clave-guardar"
          className="rounded-full w-full px-5 py-3 bg-alpha-black text-white font-mono-alpha text-[11px] uppercase tracking-widest disabled:opacity-40"
        >
          {guardando ? "Guardando…" : "Guardar y entrar"}
        </button>
      </form>
    </motion.main>
  );
}
